import React from 'react';

import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    SortableTable,
    SortableTableHeaderCell,
} from '@bb-ui/react-library/dist/components';
import { UserListLicenseOverview } from './UserListLicenseOverview';
import {
    SORT_COLUMNS,
    TableViewSettings,
    useUserListTableData,
} from './useUserListTableData';
import { useStyles } from './UserList.styles';
import { PagingControls } from '../../../PagingControls/PagingControls';
import { UserListTableFilters } from './UserListTableFilters';
import { UserListLoading } from './UserListLoading';
import { UserListError } from './UserListError';
import { UserDeleteDialog } from '../DeleteDialog/UserDeleteDialog';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { UserListTableActions } from './UserListTableActions';
import { useFilters } from './useFilters';
import { useUser } from '../../../../hooks/useUser';

export const UserList = () => {
    const {
        nameFilter,
        setNameFilter,
        roleFilter,
        setRoleFilter,
        idpIdFilter,
        setIdpIdFilter,
        page,
        setPage,
        sort,
        setSort,
    } = useFilters();

    const history = useHistory();

    const pageSize = 10;

    const tableViewSettings: TableViewSettings = React.useMemo(
        () => ({
            filter: {
                name: nameFilter,
                role: roleFilter,
                idpId: idpIdFilter,
            },
            paging: [page, pageSize],
            sort,
        }),
        [idpIdFilter, nameFilter, page, roleFilter, sort],
    );

    const { userPage, allUsersPostFilter, allUsers, loaded, error } =
        useUserListTableData(tableViewSettings);

    const classes = useStyles();
    const { t } = useTranslation();

    const columnKeys = SORT_COLUMNS;
    const columnLabelMap: Record<(typeof columnKeys)[number], string> = {
        // how each of the rendered columns gets translated -> settings.licenseManagement.tableHeaders.{value}
        userName: 'username',
        rolesString: 'role',
        idpName: 'identityProvider',
        lastAccessString: 'lastAccess',
    };

    const pageList = React.useMemo(
        // array like [1, 2, 3] when there's 3 pages
        () =>
            Array.from(
                { length: Math.ceil(allUsersPostFilter.length / pageSize) },
                (_, i) => i + 1,
            ),
        [allUsersPostFilter.length],
    );

    const [userToDelete, setUserToDelete] = React.useState<User | undefined>(
        undefined,
    );

    const sessionUser = useUser();

    if (error) {
        return <UserListError />;
    }

    if (!loaded) {
        return (
            <UserListLoading columns={columnKeys.length + 1} rows={pageSize} />
        );
    }

    return (
        <>
            <UserDeleteDialog
                userToDelete={userToDelete}
                setUserToDelete={setUserToDelete}
            />
            <UserListLicenseOverview />
            <UserListTableFilters
                idpIdFilter={idpIdFilter}
                setIdpIdFilter={setIdpIdFilter}
                nameFilter={nameFilter}
                setNameFilter={setNameFilter}
                roleFilter={roleFilter}
                setPage={setPage}
                setRoleFilter={setRoleFilter}
                userCount={allUsers?.length || 0}
            />
            <div className={classes.tableContainer}>
                <SortableTable
                    aria-colcount={columnKeys.length + 1}
                    aria-rowcount={allUsers?.length}
                    getAriaSortMessage={(column: any, direction: any) => {
                        const columnName = t(
                            `settings.licenseManagement.tableHeaders.${columnLabelMap[column as (typeof columnKeys)[number]]}`,
                        );

                        return t(`general.tableSortAnnouncement.${direction}`, {
                            column: columnName,
                        });
                    }}
                    id="license-management-table"
                    onSortChanged={params => {
                        setPage(1);
                        if (params.sortColumnId) {
                            setSort([
                                params.sortColumnId,
                                params.sortDirection,
                            ] as any);
                        } else {
                            setSort(undefined);
                        }
                        return true;
                    }}
                    disableUnsortedState={true}
                    initialSortColumnId={sort ? sort[0] : columnKeys[0]}
                    initialSortDirection={sort ? sort[1] : 'desc'}
                >
                    <TableHead>
                        <TableRow>
                            {columnKeys.map(label => (
                                <SortableTableHeaderCell
                                    key={label}
                                    columnId={label}
                                    id={label}
                                >
                                    {t(
                                        `settings.licenseManagement.tableHeaders.${columnLabelMap[label]}`,
                                    )}
                                </SortableTableHeaderCell>
                            ))}

                            <TableCell
                                role="columnheader"
                                style={{
                                    textAlign: 'right',
                                }}
                            >
                                {t(
                                    `settings.licenseManagement.tableHeaders.actions`,
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allUsersPostFilter.length > 0 ? (
                            userPage.map(u => (
                                <TableRow
                                    role="row"
                                    data-testid="user-table-row"
                                    key={u.userId}
                                >
                                    {columnKeys.map(key => (
                                        <TableCell
                                            role="gridcell"
                                            key={key}
                                            data-testid={`${key}-${u.userId}`}
                                        >
                                            {u[key]}
                                        </TableCell>
                                    ))}
                                    <UserListTableActions
                                        user={u}
                                        onDeleteButtonClick={setUserToDelete}
                                        onEditButtonClick={() => {
                                            history.push(
                                                `/settings/users/${encodeURIComponent(u.userId)}/edit`,
                                            );
                                        }}
                                        sessionUserId={sessionUser?.userId}
                                    />
                                </TableRow>
                            ))
                        ) : (
                            <TableRow
                                key="noData"
                                data-testid="user-table-has-no-data"
                            >
                                <TableCell
                                    colSpan={columnKeys.length}
                                    className={classes.noDataCell}
                                >
                                    {t('errors.noData')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </SortableTable>
            </div>
            {allUsersPostFilter.length > 0 ? (
                <div className={classes.pagination}>
                    <PagingControls
                        pageNumberOptions={pageList}
                        pageNumber={page}
                        updateCurrentPage={setPage}
                    />
                </div>
            ) : null}
        </>
    );
};
